// Shared Toggle-Logik für den "öffentlicher Auftraggeber"-Leitweg-ID-Block (ECOM-3665).
// Bewusst plain DOM (kein jQuery) — neue Dateien dürfen kein jQuery einführen.
// Die aufrufenden (jQuery-basierten) Dateien reichen ein HTMLElement (Form/Container) herein.

/**
 * Prüft, ob im übergebenen Form/Container aktuell Deutschland (ISO-2 "DE") als Land gewählt ist.
 * Die Leitweg-ID ist nur für deutsche Adressen ein Pflichtfeld.
 */
function isGermanySelected(formEl: HTMLElement): boolean {
    const countrySelect = formEl.querySelector<HTMLSelectElement>('.CountryListGeschaeftlich');
    return (countrySelect?.value ?? '').trim().toUpperCase() === 'DE';
}

/**
 * Liest den aktuellen "öffentlicher Auftraggeber"-Zustand aus dem Hidden-Field des
 * übergebenen Form/Containers.
 *
 * Bewusst case-insensitiv: Razor rendert Bool-Hidden-Fields als "True"/"False",
 * `applyLeitwegIdState` normalisiert sie danach auf "true"/"false". Ein Vergleich
 * gegen "true" allein würde den von Razor gerenderten Initialzustand als `false`
 * lesen und damit eine "öffentlicher Auftraggeber"-Adresse still zu "geschäftlich"
 * degradieren (inkl. Leeren der Leitweg-ID).
 */
export function isPublicAuthoritySelected(formEl: HTMLElement | null | undefined): boolean {
    const field = formEl?.querySelector<HTMLInputElement>('.input_IsPublicAuthority');
    return (field?.value ?? '').trim().toLowerCase() === 'true';
}

/**
 * Setzt bzw. entfernt die Pflichtfeld-Markierung (Stern am Label + `required` am Input).
 * KuP-Adressen werden read-only (ohne .input_LeitwegId) gerendert — dann gibt es kein Input.
 */
function setMandatory(label: HTMLElement | null, input: HTMLInputElement | null, mandatory: boolean): void {
    if (label) {
        if (mandatory) {
            label.classList.add('mandatory');
            if (!label.querySelector('sup')) {
                const sup = document.createElement('sup');
                sup.textContent = '*';
                label.append(' ', sup);
            }
        } else {
            label.classList.remove('mandatory');
            label.querySelector('sup')?.remove();
        }
    }

    if (input) {
        if (mandatory) {
            input.setAttribute('required', 'required');
        } else {
            input.removeAttribute('required');
        }
    }
}

/**
 * Steuert die gegenseitig exklusiven Felder der geschäftlichen Rechnungsadresse:
 * Blendet den Leitweg-ID-Block ein/aus inkl. Pflichtfeld-Markierung (Stern + `required`), blendet
 * die E-Invoice-ID-Row invers dazu ein/aus (optional, ohne Pflicht-Markierung), blendet die
 * E-Mail-Rechnungsversand-Row invers dazu ein/aus und schreibt das `IsPublicAuthority`-Hidden-Field
 * abhängig vom übergebenen Zustand. Die Pflichtfeld-Validierung der E-Mail läuft über das statische
 * data-val-required (unobtrusive) zusammen mit dem jQuery-Validate-Default `ignore: ":hidden"` — ein
 * ausgeblendetes Feld wird nicht validiert, daher ist dort kein dynamisches HTML5-`required` nötig.
 *
 * Die Leitweg-ID gilt nur für deutsche öffentliche Auftraggeber: Das Feld wird ausschließlich
 * angezeigt (und ist dann Pflicht), wenn öffentlicher Auftraggeber gewählt ist UND das Land
 * Deutschland ("DE") ist. Bei anderen Ländern wird das Feld komplett ausgeblendet und geleert.
 * Deshalb wird bei jedem Aufruf das aktuell gewählte Land mit ausgewertet — die aufrufenden
 * Dateien rufen die Funktion daher auch bei Länderwechsel erneut auf.
 *
 * @param formEl Das umschließende Form-/Container-Element, in dem die Felder gesucht werden.
 * @param isPublicAuthority `true` = öffentlicher Auftraggeber (Leitweg-ID sichtbar/Pflicht, E-Invoice/E-Mail versteckt), `false` = geschäftlich (E-Invoice optional + E-Mail Pflicht sichtbar).
 */
export function applyLeitwegIdState(formEl: HTMLElement | null | undefined, isPublicAuthority: boolean): void {
    if (!formEl) {
        return;
    }

    const row = formEl.querySelector<HTMLElement>('.js-leitwegid-row');
    const isPublicAuthorityField = formEl.querySelector<HTMLInputElement>('.input_IsPublicAuthority');

    if (isPublicAuthorityField) {
        isPublicAuthorityField.value = isPublicAuthority ? 'true' : 'false';
    }

    // E-Invoice-Row invers zur Leitweg-Row ein-/ausblenden (ECOM-3668): sichtbar bei
    // "geschäftlich", versteckt bei "öffentlicher Auftraggeber". Das Feld bleibt optional
    // (kein required / kein Pflicht-Stern); beim Ausblenden den Wert leeren. Bewusst vor dem
    // Leitweg-row-Guard, damit der E-Invoice-Toggle auch ohne Leitweg-Row immer läuft.
    const eInvoiceRow = formEl.querySelector<HTMLElement>('.js-einvoiceid-row');
    if (eInvoiceRow) {
        const eInvoiceInput = eInvoiceRow.querySelector<HTMLInputElement>('.input_EInvoiceId');
        if (isPublicAuthority) {
            eInvoiceRow.classList.add('hide');
            if (eInvoiceInput) {
                eInvoiceInput.value = '';
            }
        } else {
            eInvoiceRow.classList.remove('hide');
        }
    }

    // E-Mail-Rechnungsversand-Row invers zur Leitweg-Row ein-/ausblenden (ECOM-3668): sichtbar
    // und Pflicht bei "geschäftlich", versteckt bei "öffentlicher Auftraggeber". Die Pflicht wird
    // über das statische data-val-required (unobtrusive) + `ignore: ":hidden"` gesteuert — ein
    // ausgeblendetes Feld wird nicht validiert, daher kein dynamisches HTML5-`required`. Beim
    // Ausblenden den Wert leeren; der Pflicht-Stern ist statisch gerendert und wird mitversteckt.
    const eInvoiceEmailRow = formEl.querySelector<HTMLElement>('.js-einvoiceemail-row');
    if (eInvoiceEmailRow) {
        const eInvoiceEmailInput = eInvoiceEmailRow.querySelector<HTMLInputElement>('.input_EInvoiceEmail');
        if (isPublicAuthority) {
            eInvoiceEmailRow.classList.add('hide');
            if (eInvoiceEmailInput) {
                eInvoiceEmailInput.value = '';
            }
        } else {
            eInvoiceEmailRow.classList.remove('hide');
        }
    }

    if (!row) {
        return;
    }

    const input = row.querySelector<HTMLInputElement>('.input_LeitwegId');
    const label = row.querySelector<HTMLElement>('label, .entry-label');

    // Feld nur für deutsche öffentliche Auftraggeber sichtbar und dann Pflicht (Stern + required).
    const showLeitwegId = isPublicAuthority && isGermanySelected(formEl);

    if (showLeitwegId) {
        row.classList.remove('hide');
        setMandatory(label, input, true);
    } else {
        row.classList.add('hide');
        setMandatory(label, input, false);

        // Ausgeblendetes Feld leeren, damit kein veralteter Wert (z.B. nach Länderwechsel) mitgeht.
        // KuP-Adressen werden read-only (ohne .input_LeitwegId) gerendert — dann gibt es kein Input.
        if (input) {
            input.value = '';
        }
    }
}
